import { focusOnPointAnimated } from "../../utils/viewerHelpers.js";
import {
  MapModeEnum,
  searchPinActions,
  mapActions,
  uiActions,
} from "../reducers/mapPanZoomReducer.js";
import {
  showFloor,
  resetIndoorLayer,
  updateSearchPinVisibility,
  getCurrentBuildingId,
} from "./floorManager.js";

/**
 * クリックされたオブジェクトの種別と詳細を判定する
 * @param {string[]} currentBuildingArray 建物配列
 * @param {Record<string, object>} currentMapJson マップJSON
 * @param {Array} currentFloorArray フロア配列
 * @param {Array} currentRoomArray ルーム配列
 * @param {Array} mapIconJson マーカーアイコンJSON
 * @param {number} currentCampus 現在のキャンパス
 * @param {string} targetId クリックされた要素ID
 * @returns {{ type: string, detail?: object }} 判定結果
 */
function getClickedObjectDetail(
  currentBuildingArray,
  currentMapJson,
  currentFloorArray,
  currentRoomArray,
  mapIconJson,
  currentCampus,
  targetId,
) {
  // building
  const buildingDetail = currentBuildingArray.find(
    (value) =>
      currentMapJson[value].building_pin === targetId ||
      currentMapJson[value].building_base === targetId,
  );
  if (buildingDetail) {
    return { type: "building", detail: currentMapJson[buildingDetail] };
  }

  // floor
  const floorDetail = currentFloorArray.find(
    (value) => value.floor_id === targetId,
  );
  if (floorDetail) {
    return { type: "floor", detail: floorDetail };
  }

  // room
  const roomDetail = currentRoomArray.find(
    (value) => value.room_id === targetId,
  );
  if (roomDetail) {
    return { type: "room", detail: roomDetail };
  }

  // marker
  const markerDetail = getMarkerDetail(mapIconJson, currentCampus, targetId);
  if (markerDetail) {
    return { type: "marker", detail: markerDetail };
  }

  return { type: "others" };
}

/**
 * マーカーの詳細を取得
 * @param {Array} mapIconJson マーカーアイコンJSON
 * @param {number} currentCampus 現在のキャンパス
 * @param {string} targetId マーカーID
 * @returns {object | null} マーカー情報
 */
function getMarkerDetail(mapIconJson, currentCampus, targetId) {
  const campusMarkers = mapIconJson[currentCampus - 1]?.markers || [];
  return campusMarkers.find((marker) => marker?.id === targetId) || null;
}

/**
 * 指定要素の中央にフォーカスしてピンをドロップ
 * @param {object} svgRootRef SVGルートref
 * @param {object} viewerRef ビューアref
 * @param {object} floorStoreRef フロアストアref
 * @param {function} dispatch 統合dispatch
 * @param {string} elementId 要素ID
 */
function focusAndDropPin(
  svgRootRef,
  viewerRef,
  floorStoreRef,
  dispatch,
  elementId,
) {
  const svgRoot = svgRootRef.current;
  const viewer = viewerRef.current;
  const floorStore = floorStoreRef.current;

  if (!elementId) return;
  const elem = document.getElementById(elementId);
  if (!elem) return;

  const cr = elem.getBoundingClientRect();

  if (!svgRoot || !viewer || !cr) return;

  const rectLeft = cr.x;
  const rectTop = cr.y;
  const screenCX = rectLeft + cr.width / 2;
  const screenCY = rectTop + cr.height / 2;

  const pt = svgRoot.createSVGPoint();
  pt.x = screenCX;
  pt.y = screenCY;

  const screenCTM = svgRoot.firstElementChild?.getScreenCTM();
  if (!screenCTM) return;

  const svgP = pt.matrixTransform(screenCTM.inverse());

  focusOnPointAnimated(viewer, svgRoot, svgP.x, svgP.y, 6.0, 800);

  // ピンをクリアしてドロップ
  dispatch(searchPinActions.clear());
  const currentFloorId = floorStore.mountedFloorId;
  const currentBuildingId = getCurrentBuildingId(floorStore);
  dispatch(
    searchPinActions.drop(svgP.x, svgP.y, currentFloorId, currentBuildingId),
  );
}

/**
 * Normal状態の物を選択（ex: 建物, 平面アイコン）
 * @param {object} floorStoreRef フロアストアref
 * @param {object} svgRootRef SVGルートref
 * @param {object} viewerRef ビューアref
 * @param {function} dispatch 統合dispatch
 * @param {object} mapSlice currentMode, currentCampus
 * @param {string} targetId フォーカス対象ID
 */
function selectNormal(
  floorStoreRef,
  svgRootRef,
  viewerRef,
  dispatch,
  mapSlice,
  targetId,
) {
  const floorStore = floorStoreRef.current;
  const svgRoot = svgRootRef.current;

  const { currentMode, currentCampus } = mapSlice;

  if (currentMode === MapModeEnum.INDOOR) {
    resetIndoorLayer(floorStore, svgRoot, currentCampus);
    dispatch(mapActions.resetIndoor());
  } else if (currentMode !== MapModeEnum.NORMAL) {
    dispatch(mapActions.setMode(MapModeEnum.NORMAL));
  }

  focusAndDropPin(svgRootRef, viewerRef, floorStoreRef, dispatch, targetId);
}

/**
 * Indoor状態の物を選択（ex: 部屋、アイコン）
 * @param {object} floorStoreRef フロアストアref
 * @param {object} svgRootRef SVGルートref
 * @param {object} viewerRef ビューアref
 * @param {function} dispatch 統合dispatch
 * @param {number} currentCampus 現在のキャンパス
 * @param {object} searchPin 検索ピンのstate
 * @param {object} building 選択対象の建物
 * @param {string} targetFloorId 遷移先フロアID
 * @param {string} targetElementId フォーカス対象要素ID
 */
function selectIndoor(
  floorStoreRef,
  svgRootRef,
  viewerRef,
  dispatch,
  currentCampus,
  searchPin,
  building,
  targetFloorId,
  targetElementId,
) {
  const floorStore = floorStoreRef.current;
  const svgRoot = svgRootRef.current;

  const floors = building.floors || [];
  const foundIndex = floors.findIndex((f) => f.floor_id === targetFloorId);
  const selectedIndex = foundIndex >= 0 ? foundIndex : 0;
  const selectedFloor = floors[selectedIndex];

  dispatch(mapActions.setIndoor(floors, selectedIndex));

  if (selectedFloor?.floor_id) {
    showFloor(floorStore, svgRoot, currentCampus, selectedFloor.floor_id);
    updateSearchPinVisibility(
      floorStoreRef,
      dispatch,
      searchPin,
      selectedFloor.floor_id,
    );
  }

  focusAndDropPin(
    svgRootRef,
    viewerRef,
    floorStoreRef,
    dispatch,
    targetElementId,
  );
}

/**
 * メインの選択ハンドラ: 検索タイプに応じて適切な処理を実行
 * @param {object} floorStoreRef フロアストアref
 * @param {object} svgRootRef SVGルートref
 * @param {object} viewerRef ビューアref
 * @param {function} dispatch 統合dispatch
 * @param {object} mapSlice currentBuildingArray, currentMapJson, currentFloorArray, currentRoomArray, currentCampus
 * @param {Array} mapIconJson マーカーアイコンJSON
 * @param {object} searchPin 検索ピンのstate
 * @param {string} targetId 選択対象ID
 */
export function handleSelect(
  floorStoreRef,
  svgRootRef,
  viewerRef,
  dispatch,
  mapSlice,
  mapIconJson,
  searchPin,
  targetId,
) {
  const {
    currentBuildingArray,
    currentMapJson,
    currentFloorArray,
    currentRoomArray,
    currentCampus,
  } = mapSlice;

  if (!targetId) return;

  const objectDetail = getClickedObjectDetail(
    currentBuildingArray,
    currentMapJson,
    currentFloorArray,
    currentRoomArray,
    mapIconJson,
    currentCampus,
    targetId,
  );

  if (objectDetail.type === "others") return;

  if (objectDetail.type === "building") {
    selectNormal(
      floorStoreRef,
      svgRootRef,
      viewerRef,
      dispatch,
      mapSlice,
      targetId,
    );
    return;
  }

  const { building_id, floor_id } = objectDetail.detail || {};

  // 建物やフロアに紐付かないマーカーはそのまま平面マップでピンを指す
  if (objectDetail.type === "marker" && (!building_id || !floor_id)) {
    selectNormal(
      floorStoreRef,
      svgRootRef,
      viewerRef,
      dispatch,
      mapSlice,
      targetId,
    );
    return;
  }

  for (const key of currentBuildingArray) {
    const building = currentMapJson[key];
    if (building.building_id !== building_id) continue;

    if (building.indoor) {
      selectIndoor(
        floorStoreRef,
        svgRootRef,
        viewerRef,
        dispatch,
        currentCampus,
        searchPin,
        building,
        floor_id,
        targetId,
      );
    } else {
      dispatch(mapActions.setMode(MapModeEnum.NORMAL));
      focusAndDropPin(
        svgRootRef,
        viewerRef,
        floorStoreRef,
        dispatch,
        targetId,
      );
    }
    break;
  }
}

/**
 * マップ上のクリックイベントハンドラ
 * @param {object} floorStoreRef フロアストアref
 * @param {object} svgRootRef SVGルートref
 * @param {function} dispatch 統合dispatch
 * @param {object} mapSlice currentBuildingArray, currentMapJson, currentCampus
 * @param {Array} mapIconJson マーカーアイコンJSON
 * @param {object} searchPin 検索ピンのstate
 * @param {string} targetId クリックされた要素ID
 */
export function handleClick(
  floorStoreRef,
  svgRootRef,
  dispatch,
  mapSlice,
  mapIconJson,
  searchPin,
  targetId,
) {
  const floorStore = floorStoreRef.current;
  const svgRoot = svgRootRef.current;

  const { currentBuildingArray, currentMapJson, currentCampus } = mapSlice;

  const markers = mapIconJson[currentCampus - 1]?.markers || [];

  if (!targetId) return;

  currentBuildingArray.forEach((value) => {
    if (
      currentMapJson[value].building_pin === targetId ||
      currentMapJson[value].building_base === targetId
    ) {
      if (currentMapJson[value].indoor) {
        const floors = currentMapJson[value].floors || [];
        let selectedIndex = 0;

        if (
          searchPin.visible &&
          searchPin.buildingId === currentMapJson[value].building_id
        ) {
          const pinFloorIndex = floors.findIndex(
            (f) => f.floor_id === searchPin.floorId,
          );
          selectedIndex = pinFloorIndex >= 0 ? pinFloorIndex : 0;
        } else {
          const firstFloorIndex = floors.findIndex((f) =>
            /_1f|-1f/i.test(f.floor_id),
          );
          selectedIndex = firstFloorIndex >= 0 ? firstFloorIndex : 0;
        }

        dispatch(mapActions.setIndoor(floors, selectedIndex));

        const selectedFloor = floors[selectedIndex];
        if (selectedFloor?.floor_id) {
          showFloor(floorStore, svgRoot, currentCampus, selectedFloor.floor_id);
          updateSearchPinVisibility(
            floorStoreRef,
            dispatch,
            searchPin,
            selectedFloor.floor_id,
          );
        }
      } else {
        dispatch(mapActions.setMode(MapModeEnum.NORMAL));
      }
    }
  });

  markers.some((marker) => {
    if (targetId === marker.id) {
      dispatch(uiActions.openDialog(marker));
      return true;
    }
    return false;
  });
}
